import { useEffect, useRef } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Remember a few input values on the user's account. `fields` maps name -> [value, setValue].
 * `skip` (read once on mount) keeps the current values instead of restoring, e.g. when arriving via a deep link.
 * `pause` (live) stops saving, so a temporary view doesn't replace what the user chose.
 */
const useRememberedInputs = (key, fields, { skip = false, pause = false } = {}) => {
  const loaded = useRef(false);
  const pending = useRef(null);
  const paused = useRef(pause);
  paused.current = pause;
  const names = Object.keys(fields);
  const values = names.map((n) => fields[n][0]);
  const current = useRef(values);
  current.current = values;
  const snapshot = () => Object.fromEntries(names.map((n, i) => [n, current.current[i]]));
  const save = (value) => axios.put(`${API}/me/ui-state/${key}`, { value }).catch(() => {});

  useEffect(() => {
    let alive = true;
    const initial = JSON.stringify(current.current);
    if (skip) {
      loaded.current = true;
    } else {
      axios.get(`${API}/me/ui-state/${key}`)
        .then((r) => {
          if (!alive) return;
          // The user already changed something while this was loading: keep (and save) their choice.
          if (JSON.stringify(current.current) !== initial) {
            if (!paused.current) save(snapshot());
            return;
          }
          const saved = r.data?.value || {};
          names.forEach((n) => { if (saved[n] !== undefined && saved[n] !== null) fields[n][1](saved[n]); });
        })
        .catch(() => {})
        .finally(() => { if (alive) loaded.current = true; });
    }
    return () => {
      alive = false;
      if (pending.current && !paused.current) save(pending.current);  // flush the last change when leaving
    };
  }, [key]);

  useEffect(() => {
    if (!loaded.current || paused.current) return undefined;
    pending.current = snapshot();
    const id = setTimeout(() => { save(pending.current); pending.current = null; }, 800);
    return () => clearTimeout(id);
  }, values);
};

export default useRememberedInputs;
